import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  Timestamp,
  writeBatch,
} from 'firebase/firestore'
import { functionUrl } from '@/services/functionsUrl'
import { db } from '../firebase'
import { currentAccountConstraints } from './accountScope'
import { UserRole, UserRoles } from '../constants/userRoles'
import { InvitationMessages } from '../constants/messages'
import { requestNotification } from './notificationRequests'
import {
  EnhancedInvitation,
  InvitationStatus,
  InvitationMetadata,
  InvitationSource,
  GranularPermissions,
} from '../types/userTypes'
import {
  normalizeEmail,
  validateEmailForProduction,
  isDevelopment,
} from '../utils/emailNormalization'
import { PropertyUnit } from '../types/propertyTypes'
import { logger } from '@/services/logger'

// Enhanced invitation creation with metadata and role assignments
export interface CreateInvitationData {
  email: string
  firstName: string // NEW: Required
  lastName: string // NEW: Required
  phoneNumber?: string // NEW: Optional
  phoneCountry?: string // ISO country of phoneNumber (PB-66)
  role: UserRole
  hasAdminPrivileges?: boolean
  permissions?: GranularPermissions // Custom permissions for manager invitations
  assignedProperties?: string[]
  metadata?: InvitationMetadata
  organizationId?: string
  customMessage?: string
  source?: InvitationSource // Track where invitation was created from
}

/**
 * DU-004 guard: find an existing tenant in the inviting admin's pool who is
 * already actively assigned to a property. Blocks re-inviting an assigned
 * tenant, which would silently move ("steal") them to the new property.
 *
 * Rule-safe: a Pro admin can only read users in their own corporate account, so
 * we query `corporateAccountId ==` (single-field, provable) and filter email in
 * memory; a super_admin (full read) queries by email. Cross-admin conflicts —
 * a tenant under a DIFFERENT admin — are invisible to a Pro admin by design and
 * need the server-side handling tracked in DU-004; this catches the common case
 * (re-inviting a tenant already on one of the admin's own properties).
 */
const findAssignedTenantInPool = async (
  email: string,
  corporateAccountId: string | null | undefined,
  isSuperAdmin: boolean
): Promise<{ id: string; assignedPropertyId: string } | null> => {
  const target = email.trim().toLowerCase()
  try {
    const usersRef = collection(db, 'users')
    const snap = isSuperAdmin
      ? await getDocs(query(usersRef, where('email', '==', email.trim())))
      : corporateAccountId
        ? await getDocs(
            query(
              usersRef,
              where('corporateAccountId', '==', corporateAccountId)
            )
          )
        : null
    if (!snap) return null
    for (const d of snap.docs) {
      const u = d.data()
      if (u.role !== 'tenant') continue
      if ((u.email || '').trim().toLowerCase() !== target) continue
      if (u.assignedPropertyId && u.accountStatus !== 'deleted') {
        return { id: d.id, assignedPropertyId: u.assignedPropertyId }
      }
    }
    return null
  } catch (err) {
    // Fail open — a lookup error must not block legitimate invites.
    console.warn('[DU-004 guard] assigned-tenant lookup failed:', err)
    return null
  }
}

/**
 * Creates an enhanced invitation with metadata and role-specific assignments
 */
export const createEnhancedInvitation = async (
  data: CreateInvitationData,
  createdByUid: string,
  corporateAccountIdOverride?: string
): Promise<{
  success: boolean
  invitation?: EnhancedInvitation
  error?: string
  /** True when blocked because the tenant is already assigned — UI shows the
   *  Contact Us link so the admin can request a move via MNTND support (DU-004). */
  contactSupport?: boolean
}> => {
  try {
    // PRODUCTION: Block email aliases
    const emailValidation = validateEmailForProduction(data.email)
    if (!emailValidation.valid) {
      return {
        success: false,
        error: emailValidation.error,
      }
    }

    // Check if active invitation already exists
    const existingInvites = await getActiveInvitationsByEmail(data.email)
    if (existingInvites.length > 0) {
      const existingInvite = existingInvites[0]
      const isSameCreator = existingInvite.invitedBy === createdByUid

      // Build informative error message
      let errorMessage =
        'An active invitation already exists for this email address.'
      if (isSameCreator) {
        errorMessage +=
          ' You sent this invitation previously. You can cancel it from your invitations list and resend.'
      } else {
        // Different admin created the invitation - provide more context
        const createdDate = existingInvite.createdAt?.toDate
          ? existingInvite.createdAt.toDate().toLocaleDateString()
          : 'unknown date'
        errorMessage += ` This invitation was sent by another user on ${createdDate}. Contact your admin to cancel the existing invitation if needed.`
      }

      return {
        success: false,
        error: errorMessage,
        // Include additional data for potential UI handling
      }
    }

    // Get the creator's corporateAccountId
    const creatorDoc = await getDoc(doc(db, 'users', createdByUid))
    if (!creatorDoc.exists()) {
      return {
        success: false,
        error: 'Creator user document not found',
      }
    }
    const creatorData = creatorDoc.data()

    // Resolve corporateAccountId:
    // Priority: explicit override > creator's own > resolve from property
    // - corporateAccountIdOverride: passed by UI when super admin selects an Admin Owner
    // - For Pro mode users: use their corporateAccountId (set to admin UID at registration)
    // - For super_admin: resolve from the assigned property's corporateAccountId
    //   Super admins don't own business objects — everything must resolve to the admin owner
    let corporateAccountId =
      corporateAccountIdOverride || creatorData.corporateAccountId
    let resolvedAdminId: string | undefined =
      corporateAccountIdOverride || undefined

    const isSuperAdmin = creatorData.role === 'super_admin'

    // New Admin Owner invitation: super admin creating a fresh root admin
    // No corporateAccountId needed — it will be set to the new user's UID at registration
    const isNewAdminOwner =
      isSuperAdmin &&
      data.role === UserRoles.ADMIN &&
      data.metadata?.isNewAdminOwner === true

    // Super admin invitations: only a super admin sends one, and it belongs to
    // no account (PB-72). The rules and onInvitationAccepted enforce it too.
    const isSuperAdminInvite = data.role === UserRoles.SUPER_ADMIN
    if (isSuperAdminInvite && !isSuperAdmin) {
      return { success: false, error: InvitationMessages.superAdminOnly }
    }

    if (!corporateAccountId && !isSuperAdmin) {
      return {
        success: false,
        error: 'Creator does not have a corporate account ID',
      }
    }

    // DU-004 guard: don't let a tenant already assigned to a property be
    // re-invited — accepting would silently move them off their current
    // property. Direct the admin to MNTND support to request a move.
    if (data.role === UserRoles.TENANT) {
      const assigned = await findAssignedTenantInPool(
        data.email,
        corporateAccountId,
        isSuperAdmin
      )
      if (assigned) {
        return {
          success: false,
          error:
            'This tenant is already assigned to a property. Moving a tenant between properties needs MNTND support — please contact us.',
          contactSupport: true,
        }
      }
    }

    // Fetch property data when assignedProperties are provided (any role)
    // Used for: admin resolution (all roles), tenant validation. (The email's
    // property name is now read by the server, MS-D11.)
    if (data.assignedProperties?.length) {
      const propertyId = data.assignedProperties[0]
      const propertyDoc = await getDoc(doc(db, 'properties', propertyId))

      if (!propertyDoc.exists()) {
        return {
          success: false,
          error: 'Selected property not found',
        }
      }

      const propertyData = propertyDoc.data()

      // When super_admin creates the invitation, resolve corporateAccountId from the property.
      // Super admin should NEVER own business objects — everything resolves to the admin owner.
      // This applies to ALL roles (tenant, manager, admin), not just tenants.
      if (isSuperAdmin) {
        const propertyCorporateId = propertyData.corporateAccountId
        if (propertyCorporateId && propertyCorporateId !== 'default') {
          corporateAccountId = propertyCorporateId
          resolvedAdminId = propertyCorporateId // In Pro mode, corporateAccountId === admin UID
          logger.debug(
            `[createEnhancedInvitation] Super admin resolved corporateAccountId from property: ${corporateAccountId} (admin owner: ${resolvedAdminId})`
          )
        } else {
          // Fallback chain: try managedBy/currentManagerId, then createdBy
          // These users should have the correct corporateAccountId (admin owner's UID)
          const candidateIds = [
            propertyData.managedBy,
            propertyData.currentManagerId,
            propertyData.createdBy,
          ].filter(
            (id): id is string => !!id && id !== createdByUid // Skip super admin's own UID
          )

          logger.debug(
            `[createEnhancedInvitation] Property ${propertyId} has corporateAccountId='${propertyData.corporateAccountId || 'null'}'. Trying fallback candidates:`,
            candidateIds
          )

          for (const candidateId of candidateIds) {
            const candidateDoc = await getDoc(doc(db, 'users', candidateId))
            if (candidateDoc.exists()) {
              const candidateData = candidateDoc.data()
              const candidateCorpId = candidateData.corporateAccountId
              if (candidateCorpId && candidateCorpId !== 'default') {
                // candidateCorpId is always the admin owner's UID in Pro mode,
                // even if the candidate is a manager (they inherit the admin's corporateAccountId)
                corporateAccountId = candidateCorpId
                resolvedAdminId = candidateCorpId
                const isDirectAdmin = candidateId === candidateCorpId
                logger.debug(
                  `[createEnhancedInvitation] Resolved via ${isDirectAdmin ? 'admin owner' : `${candidateData.role} (${candidateId})`} → admin owner: ${candidateCorpId}`
                )
                break
              }
            }
          }

          // If still unresolved, use 'default' as last resort
          if (!corporateAccountId || corporateAccountId === 'default') {
            corporateAccountId = 'default'
            console.warn(
              `[createEnhancedInvitation] Could not resolve corporateAccountId from property ${propertyId} — no admin owner found. Using 'default'`
            )
          }
        }
      }

      // Tenant-specific: validate unit assignment if unit is specified
      if (data.role === UserRoles.TENANT && data.metadata?.unit) {
        const units = propertyData.units as PropertyUnit[] | undefined

        // If property has units[], validate that the specified unit exists
        if (units && units.length > 0) {
          const unitExists = units.some(
            (u: PropertyUnit) => u.unitNumber === data.metadata?.unit
          )

          if (!unitExists) {
            const availableUnits = units
              .map((u: PropertyUnit) => u.unitNumber)
              .join(', ')
            return {
              success: false,
              error: `Unit "${data.metadata.unit}" does not exist on this property. Available units: ${availableUnits}`,
            }
          }

          // Check if unit is already occupied
          const unit = units.find(
            (u: PropertyUnit) => u.unitNumber === data.metadata?.unit
          )
          if (unit && unit.status === 'occupied' && unit.tenantId) {
            return {
              success: false,
              error: `Unit "${data.metadata.unit}" is already occupied by another tenant`,
            }
          }
        }
      }
    } else if (
      isSuperAdmin &&
      (!corporateAccountId || corporateAccountId === 'default')
    ) {
      if (isNewAdminOwner) {
        // New Admin Owner: no corporateAccountId resolvable at invitation
        // time — the accepting user IS the corporate account owner, so the
        // value isn't known until they authenticate and acquire a UID.
        // Write a sentinel string that downstream code recognizes.
        //
        // Resolved at acceptance:
        //  - functions/index.js:onInvitationAccepted (Cloud Function,
        //    Admin SDK) — authoritative writer of security-critical fields
        //    on the user doc; substitutes the sentinel with the accepting
        //    user's UID before persisting.
        //  - This file's acceptOAuthInvitation also substitutes locally so
        //    the user-doc-create path doesn't write the sentinel.
        corporateAccountId = 'pending_registration'
      } else if (isSuperAdminInvite) {
        corporateAccountId = undefined
      } else {
        // Admin (non-owner), Manager, or Tenant without ownership context
        return {
          success: false,
          error:
            'Cannot create invitation: admin account ownership must be specified. Select an existing admin account or create a new one.',
        }
      }
    }

    // Generate invitation ID
    const invitationId = doc(collection(db, 'invitations')).id

    // Set expiration time (7 days from now)
    const expiresAt = Timestamp.fromDate(
      new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    )

    // Final guard: never write 'default' as corporateAccountId on an invitation
    // 'pending_registration' is allowed for new admin owner invitations only
    if (
      !isSuperAdminInvite &&
      (!corporateAccountId || corporateAccountId === 'default')
    ) {
      return {
        success: false,
        error:
          'Could not determine account ownership for this invitation. Ensure the property has a valid admin owner.',
      }
    }

    // Build enhanced invitation object
    const invitation: EnhancedInvitation = {
      id: invitationId,
      organizationId: data.organizationId || 'default',
      // Resolved: from creator or from property (for super_admin); none for a
      // super admin invitation
      ...(corporateAccountId && { corporateAccountId }),
      invitedBy: createdByUid,
      ...(resolvedAdminId && { resolvedAdminId }), // When super_admin creates, the resolved admin owner
      email: data.email,
      firstName: data.firstName, // NEW
      lastName: data.lastName, // NEW
      ...(data.phoneNumber && { phoneNumber: data.phoneNumber }), // Only include if provided
      ...(data.phoneNumber &&
        data.phoneCountry && { phoneCountry: data.phoneCountry }),
      role: data.role,
      hasAdminPrivileges: data.hasAdminPrivileges || false,
      ...(data.permissions && { permissions: data.permissions }),
      assignedProperties: data.assignedProperties || [],
      expiresAt,
      createdAt: serverTimestamp() as Timestamp,
      status: 'pending',
      source: data.source || 'unknown', // Track where invitation was created
      metadata: {
        ...(data.metadata?.department && {
          department: data.metadata.department,
        }),
        ...(data.customMessage && { customMessage: data.customMessage }),
        ...(data.metadata?.customMessage && {
          customMessage: data.metadata.customMessage,
        }),
        ...(data.metadata?.restrictions && {
          restrictions: data.metadata.restrictions,
        }),
        onboardingFlow: getOnboardingFlow(data.role),
        requiredFields: getRequiredFields(data.role),
        ...(data.metadata?.unit && { unit: data.metadata.unit }),
      },
    }

    // Log invitation resolution chain
    logger.debug('[createEnhancedInvitation] Resolution chain:', {
      creatorUid: createdByUid,
      creatorRole: creatorData.role,
      resolvedCorporateAccountId: corporateAccountId,
      resolvedAdminId: resolvedAdminId || 'N/A (creator is admin)',
      inviteeEmail: data.email,
      inviteeRole: data.role,
      assignedProperties: data.assignedProperties,
    })

    // Create invitation document
    const invitationRef = doc(db, 'invitations', invitationId)
    await setDoc(
      invitationRef,
      invitation as unknown as Record<string, unknown>
    )

    // Invitation email: the server reads the address from the invitation
    // record and sends it (MS-D11, PB-07).
    try {
      await requestNotification('invitation', { invitationId })
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError)
      // Don't fail invitation creation if email fails - invitation still exists
    }

    // The saved record has the server's time; the copy handed back still
    // holds the serverTimestamp() placeholder, which showed as "Sent:
    // undefined undefined NaN" until a reload (PB-98 2). Give it local now.
    return {
      success: true,
      invitation: { ...invitation, createdAt: Timestamp.now() },
    }
  } catch (error) {
    console.error('Error creating enhanced invitation:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * Gets a single invitation by ID
 */
export const getInvitationById = async (
  invitationId: string
): Promise<EnhancedInvitation | null> => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId)
    const invitationSnap = await getDoc(invitationRef)

    if (!invitationSnap.exists()) {
      return null
    }

    return {
      id: invitationSnap.id,
      ...invitationSnap.data(),
    } as EnhancedInvitation
  } catch (error) {
    console.error('Error getting invitation by ID:', error)
    return null
  }
}

/**
 * Gets active invitations by email
 * Supports email normalization for Gmail alias matching
 * Example: Can find invitation for user+alias@gmail.com when searching with user@gmail.com
 */
export const getActiveInvitationsByEmail = async (
  email: string
): Promise<EnhancedInvitation[]> => {
  try {
    logger.debug('[getActiveInvitationsByEmail] Searching for email:', email)

    // Get the normalized version of the email
    const normalizedEmail = normalizeEmail(email)
    logger.debug(
      '[getActiveInvitationsByEmail] Normalized email:',
      normalizedEmail
    )

    // First, try exact match
    // RS-2: scoped to the caller's account — other accounts' invitations
    // aren't readable (and listing pending invitations is no longer public).
    const scope = await currentAccountConstraints()
    const exactQuery = query(
      collection(db, 'invitations'),
      ...scope,
      where('email', '==', email),
      where('status', '==', 'pending')
    )

    const exactSnapshot = await getDocs(exactQuery)
    const invitations: EnhancedInvitation[] = []

    exactSnapshot.forEach(doc => {
      invitations.push({
        id: doc.id,
        ...doc.data(),
      } as EnhancedInvitation)
    })

    logger.debug(
      '[getActiveInvitationsByEmail] Exact matches found:',
      invitations.length
    )

    // If normalized email is different, also search by normalized email
    // This handles the case where invitation was sent to user+alias@gmail.com
    // but OAuth provider returns user@gmail.com
    if (normalizedEmail !== email.toLowerCase()) {
      logger.debug(
        '[getActiveInvitationsByEmail] Searching by normalized email (different from input)...'
      )

      // Get all pending invitations and filter by normalized email
      const allPendingQuery = query(
        collection(db, 'invitations'),
        ...scope,
        where('status', '==', 'pending')
      )

      const allSnapshot = await getDocs(allPendingQuery)

      allSnapshot.forEach(doc => {
        const invitationData = doc.data() as EnhancedInvitation
        const invitationNormalizedEmail = normalizeEmail(invitationData.email)

        // If normalized emails match and we haven't already added this invitation
        if (
          invitationNormalizedEmail === normalizedEmail &&
          !invitations.find(inv => inv.id === doc.id)
        ) {
          logger.debug(
            '[getActiveInvitationsByEmail] Found normalized match:',
            {
              invitationEmail: invitationData.email,
              normalizedInvitationEmail: invitationNormalizedEmail,
              searchEmail: email,
              normalizedSearchEmail: normalizedEmail,
            }
          )

          invitations.push({
            ...invitationData,
            id: doc.id,
          } as EnhancedInvitation)
        }
      })
    }

    logger.debug(
      '[getActiveInvitationsByEmail] Total matches found:',
      invitations.length
    )

    // Filter out expired invitations
    const now = new Date()
    const activeInvitations = invitations.filter(
      inv => inv.expiresAt.toDate() > now
    )

    logger.debug(
      '[getActiveInvitationsByEmail] Active (non-expired) invitations:',
      activeInvitations.length
    )

    return activeInvitations
  } catch (error) {
    console.error('Error getting invitations by email:', error)
    return []
  }
}

/**
 * Gets all invitations created by a user with enhanced filtering
 */
export const getInvitationsByCreator = async (
  creatorUid: string,
  filters?: {
    status?: InvitationStatus
    role?: UserRole
    organizationId?: string
  }
): Promise<EnhancedInvitation[]> => {
  try {
    let invitationsQuery = query(
      collection(db, 'invitations'),
      where('invitedBy', '==', creatorUid)
    )

    // Add filters
    if (filters?.status) {
      invitationsQuery = query(
        invitationsQuery,
        where('status', '==', filters.status)
      )
    }

    if (filters?.role) {
      invitationsQuery = query(
        invitationsQuery,
        where('role', '==', filters.role)
      )
    }

    if (filters?.organizationId) {
      invitationsQuery = query(
        invitationsQuery,
        where('organizationId', '==', filters.organizationId)
      )
    }

    const querySnapshot = await getDocs(invitationsQuery)
    const invitations: EnhancedInvitation[] = []

    querySnapshot.forEach(doc => {
      invitations.push({
        id: doc.id,
        ...doc.data(),
      } as EnhancedInvitation)
    })

    return invitations.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)
  } catch (error) {
    console.error('Error getting invitations by creator:', error)
    return []
  }
}

/**
 * Verifies and retrieves invitation by ID or email
 */
export const verifyEnhancedInvitation = async (
  identifier: string,
  isEmail: boolean = false
): Promise<{
  success: boolean
  invitation?: EnhancedInvitation
  error?: string
}> => {
  try {
    let invitation: EnhancedInvitation | null = null

    if (isEmail) {
      const invitations = await getActiveInvitationsByEmail(identifier)
      if (invitations.length === 0) {
        return {
          success: false,
          error: 'No active invitation found for this email',
        }
      }
      invitation = invitations[0] // Get most recent active invitation
    } else {
      const invitationDoc = await getDoc(doc(db, 'invitations', identifier))
      if (!invitationDoc.exists()) {
        return { success: false, error: 'Invitation not found' }
      }
      invitation = {
        ...invitationDoc.data(),
        id: invitationDoc.id,
      } as EnhancedInvitation
    }

    // Check if invitation is valid
    if (invitation.status !== 'pending') {
      // Provide specific error messages based on status
      const errorMessages = {
        accepted: 'This invitation has already been accepted',
        expired: 'This invitation has expired',
        revoked: 'This invitation has been cancelled',
      }
      return {
        success: false,
        error:
          errorMessages[invitation.status] || 'Invitation is no longer active',
      }
    }

    // Check if invitation has expired
    if (invitation.expiresAt.toDate() < new Date()) {
      // Mark as expired
      await updateInvitationStatus(invitation.id, 'expired')
      return {
        success: false,
        error: 'This invitation has expired (created more than 7 days ago)',
      }
    }

    return { success: true, invitation }
  } catch (error) {
    console.error('Error verifying invitation:', error)
    return { success: false, error: 'Failed to verify invitation' }
  }
}

/**
 * Updates invitation status
 */
export const updateInvitationStatus = async (
  invitationId: string,
  status: InvitationStatus,
  additionalData?: {
    acceptedBy?: string
    acceptedAt?: Timestamp
    rejectionReason?: string
  }
): Promise<{ success: boolean; error?: string }> => {
  try {
    const invitationRef = doc(db, 'invitations', invitationId)

    const updateData: Record<string, unknown> = {
      status,
      updatedAt: serverTimestamp(),
    }

    if (additionalData) {
      Object.assign(updateData, additionalData)
    }

    await updateDoc(invitationRef, updateData)

    return { success: true }
  } catch (error) {
    console.error('Error updating invitation status:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * Accepts an invitation for an existing OAuth/Magic Link user
 * Use this when the user already has a Firebase account (Google, Apple, Amazon, Magic Link)
 * Supports email normalization for Gmail alias matching
 */
/**
 * A super admin invitation is accepted by the server, which writes the record
 * and marks the invitation accepted: the rules never let a browser give anyone
 * super_admin (RS-1, PB-72). Waits for the answer, unlike the other roles.
 */
const acceptSuperAdminInvitation = async (
  invitationId: string
): Promise<{ success: boolean; error?: string }> => {
  const { auth: firebaseAuth } = await import('../firebase')
  const idToken = await firebaseAuth.currentUser?.getIdToken()
  if (!idToken) return { success: false, error: 'Not signed in' }
  const response = await fetch(functionUrl('onInvitationAccepted'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ invitationId }),
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string
    }
    console.error('[acceptSuperAdminInvitation] refused', response.status, body)
    return { success: false, error: body.error || 'Could not accept' }
  }
  return { success: true }
}

export const acceptOAuthInvitation = async (
  invitationId: string,
  userId: string,
  userEmail?: string
): Promise<{ success: boolean; error?: string }> => {
  let userDocCreated = false

  try {
    logger.debug('[acceptOAuthInvitation] Starting', {
      invitationId,
      userId,
      userEmail,
    })
    logger.debug(
      '[acceptOAuthInvitation] Environment:',
      isDevelopment() ? 'DEVELOPMENT' : 'PRODUCTION'
    )
    logger.debug(
      '[acceptOAuthInvitation] Email normalization enabled:',
      isDevelopment()
    )

    // Get the OAuth user's email - prefer passed email, fallback to Firestore
    logger.debug('[acceptOAuthInvitation] Step 1: Fetching user email...')
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    // Use provided email first, then check Firestore document
    const finalUserEmail =
      userEmail || (userDoc.exists() ? userDoc.data()?.email : null)
    logger.debug(
      '[acceptOAuthInvitation] User email (from param or Firestore):',
      finalUserEmail
    )

    // Verify invitation is still valid
    logger.debug('[acceptOAuthInvitation] Step 2: Verifying invitation...')
    const verificationResult = await verifyEnhancedInvitation(invitationId)
    if (!verificationResult.success || !verificationResult.invitation) {
      console.error(
        '[acceptOAuthInvitation] ❌ Verification failed:',
        verificationResult.error
      )
      return { success: false, error: verificationResult.error }
    }
    logger.debug('[acceptOAuthInvitation] ✅ Invitation verified')

    const invitation = verificationResult.invitation
    if (invitation.role === UserRoles.SUPER_ADMIN) {
      return acceptSuperAdminInvitation(invitationId)
    }
    logger.debug('[acceptOAuthInvitation] Invitation data:', {
      email: invitation.email,
      role: invitation.role,
      corporateAccountId: invitation.corporateAccountId,
    })

    // Validate we have all required data before creating user
    if (!invitation.corporateAccountId) {
      console.error(
        '[acceptOAuthInvitation] ❌ Invitation missing corporateAccountId'
      )
      throw new Error(
        'Invitation missing corporateAccountId - cannot create user'
      )
    }

    // Normalize both emails for comparison
    const normalizedInvitationEmail = normalizeEmail(invitation.email)
    const normalizedUserEmail = finalUserEmail
      ? normalizeEmail(finalUserEmail)
      : null

    logger.debug('[acceptOAuthInvitation] Email comparison:', {
      invitationEmail: invitation.email,
      normalizedInvitationEmail,
      userEmail: finalUserEmail,
      normalizedUserEmail,
      match: normalizedUserEmail === normalizedInvitationEmail,
    })

    // Verify that the user's email matches the invitation email (using normalized comparison)
    if (finalUserEmail && normalizedUserEmail !== normalizedInvitationEmail) {
      console.error('[acceptOAuthInvitation] ❌ Email mismatch:', {
        userEmail: finalUserEmail,
        invitationEmail: invitation.email,
        normalizedUserEmail,
        normalizedInvitationEmail,
      })
      return {
        success: false,
        error: 'This invitation was sent to a different email address',
      }
    }

    // Check if user document exists, if not create it
    logger.debug(
      '[acceptOAuthInvitation] Step 3: Checking if user document exists...'
    )
    logger.debug('[acceptOAuthInvitation] User exists:', userDoc.exists())

    // Resolve the effective parent and corporateAccountId for this user.
    // If the invitation was created by super_admin with resolvedAdminId, use that.
    // If not (legacy invitation or resolution failed at creation), try resolving from the property now.
    let resolvedAdminId = invitation.resolvedAdminId || null
    let effectiveCorporateAccountId = invitation.corporateAccountId || null

    if (
      (!resolvedAdminId ||
        !effectiveCorporateAccountId ||
        effectiveCorporateAccountId === 'default') &&
      invitation.assignedProperties?.length
    ) {
      logger.debug(
        '[acceptOAuthInvitation] Invitation has stale/missing admin resolution — resolving from property...'
      )
      const propertyId = invitation.assignedProperties[0]
      try {
        const propertySnap = await getDoc(doc(db, 'properties', propertyId))
        if (propertySnap.exists()) {
          const propertyData = propertySnap.data()

          // Try property's corporateAccountId first
          const propCorpId = propertyData.corporateAccountId
          if (propCorpId && propCorpId !== 'default') {
            resolvedAdminId = propCorpId
            effectiveCorporateAccountId = propCorpId
            logger.debug(
              `[acceptOAuthInvitation] Resolved from property corporateAccountId: ${propCorpId}`
            )
          } else {
            // Fallback: try managedBy, currentManagerId, createdBy
            const candidates = [
              propertyData.managedBy,
              propertyData.currentManagerId,
              propertyData.createdBy,
            ].filter((id): id is string => !!id)

            for (const candidateId of candidates) {
              const candidateDoc = await getDoc(doc(db, 'users', candidateId))
              if (candidateDoc.exists()) {
                const candidateData = candidateDoc.data()
                const corpId = candidateData.corporateAccountId
                if (corpId && corpId !== 'default') {
                  // corporateAccountId is always the admin owner's UID in Pro mode,
                  // even if candidate is a manager (they inherit admin's corporateAccountId)
                  resolvedAdminId = corpId
                  effectiveCorporateAccountId = corpId
                  const isDirectAdmin = candidateId === corpId
                  logger.debug(
                    `[acceptOAuthInvitation] Resolved via ${isDirectAdmin ? 'admin owner' : `${candidateData.role} (${candidateId})`} → admin owner: ${corpId}`
                  )
                  break
                }
              }
            }
          }
        }
      } catch (resolveError) {
        console.warn(
          '[acceptOAuthInvitation] Failed to resolve admin from property:',
          resolveError
        )
      }
    }

    const effectiveParentUserId = resolvedAdminId || invitation.invitedBy
    const effectiveCreatedBy = invitation.invitedBy // Always track who actually created it

    // Override invitation's corporateAccountId if we resolved a better one
    if (
      effectiveCorporateAccountId &&
      effectiveCorporateAccountId !== 'default'
    ) {
      invitation.corporateAccountId = effectiveCorporateAccountId
    }

    // New Admin Owner invitations carry corporateAccountId='pending_registration'
    // (sentinel written by createEnhancedInvitation when super_admin selects
    // "Create New Account" + admin role — there's no admin owner yet to inherit
    // from). Resolve to the accepting user's UID: this user IS the new admin
    // owner, so they're their own corporate account (Pro mode self-referential
    // pattern). parentUserId stays as invitation.invitedBy (super_admin) — that's
    // semantically correct as the hierarchical parent.
    if (
      invitation.corporateAccountId === 'pending_registration' &&
      invitation.role === UserRoles.ADMIN
    ) {
      logger.debug(
        '[acceptOAuthInvitation] Resolving pending_registration sentinel → user.uid for new admin owner:',
        userId
      )
      invitation.corporateAccountId = userId
      effectiveCorporateAccountId = userId
    }

    logger.debug('[acceptOAuthInvitation] Resolved ownership:', {
      invitedBy: invitation.invitedBy,
      resolvedAdminId: resolvedAdminId || 'N/A',
      effectiveParentUserId,
      corporateAccountId: invitation.corporateAccountId,
    })

    // Guard: never create/update a user doc with corporateAccountId 'default'
    // or missing. We deliberately do NOT call the full
    // `assertCorporateAccountIdNotSuperAdmin` guard here — that guard reads
    // `users/{corporateAccountId}` to check the proposed owner's role, but
    // the accepting user (a brand-new tenant) does not have permission to
    // read the admin owner's user doc. Ownership cascades from property →
    // invitation → tenant, and the property/invitation creation paths already
    // ran the full guard. By acceptance time, `invitation.corporateAccountId`
    // has been vetted upstream. See D-N-059 for the discovery.
    if (
      !invitation.corporateAccountId ||
      invitation.corporateAccountId === 'default'
    ) {
      console.error(
        '[acceptOAuthInvitation] ❌ corporateAccountId is still "default" after resolution — cannot proceed'
      )
      return {
        success: false,
        error:
          'Unable to determine account ownership. Please contact your administrator.',
      }
    }

    if (!userDoc.exists()) {
      logger.debug('[acceptOAuthInvitation] Step 4: Creating user document...')

      // Use the user's actual OAuth email if we don't have it yet
      // This ensures we use the clean email from OAuth provider
      const emailToUse = finalUserEmail || invitation.email

      const userData = {
        email: emailToUse,
        firstName: invitation.firstName,
        lastName: invitation.lastName,
        ...(invitation.phoneNumber && { phoneNumber: invitation.phoneNumber }),
        ...(invitation.phoneNumber &&
          invitation.phoneCountry && {
            phoneCountry: invitation.phoneCountry,
          }),
        role: invitation.role,
        corporateAccountId: invitation.corporateAccountId,
        // Proves membership to the rules (RS-2 step 5)
        invitationId,
        ...(invitation.permissions && { permissions: invitation.permissions }),
        accountStatus: 'active', // Set to active since user accepted invitation
        emailVerified: true, // Invitation acceptance = email verification
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: effectiveCreatedBy,
        parentUserId: effectiveParentUserId, // Links tenant to admin's org hierarchy
      }
      logger.debug('[acceptOAuthInvitation] User data to create:', userData)

      await setDoc(userRef, userData)
      userDocCreated = true
      logger.debug('[acceptOAuthInvitation] ✅ User document created')
    } else {
      logger.debug(
        '[acceptOAuthInvitation] Step 4: Updating existing user document...'
      )
      // Build update object with only fields that need to change
      // This prevents Firestore rules from blocking updates to already-set fields
      const existingData = userDoc.data()

      // Check if this is a deleted/deactivated user being reactivated
      const isReactivation =
        existingData.accountStatus === 'deleted' ||
        existingData.tenantStatus === 'deactivated'

      if (isReactivation) {
        logger.debug(
          '[acceptOAuthInvitation] 🔄 Detected deleted user - performing reactivation'
        )
      }

      const updateFields: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
        accountStatus: 'active', // Always set to active when accepting invitation
        emailVerified: true, // Invitation acceptance = email verification
      }

      // For reactivated users, clear deletion metadata
      if (isReactivation) {
        updateFields.deletedAt = null
        updateFields.deletedBy = null
        logger.debug('[acceptOAuthInvitation] Will clear deletion fields')
      }

      // Set tenantStatus for tenant role invitations
      if (invitation.role === UserRoles.TENANT) {
        updateFields.tenantStatus = 'active'
        logger.debug('[acceptOAuthInvitation] Will set tenantStatus: active')
      }

      // IMPORTANT: Always update firstName/lastName/displayName from invitation
      // The user document may have been created by signInWithGoogle() using Google's displayName,
      // but the invitation contains the names the admin specified. The invitation names take priority.
      if (invitation.firstName) {
        updateFields.firstName = invitation.firstName
        logger.debug(
          '[acceptOAuthInvitation] Will set firstName:',
          invitation.firstName
        )
      }
      if (invitation.lastName) {
        updateFields.lastName = invitation.lastName
        logger.debug(
          '[acceptOAuthInvitation] Will set lastName:',
          invitation.lastName
        )
      }
      // Also update displayName to match invitation names for consistency
      if (invitation.firstName || invitation.lastName) {
        const newDisplayName =
          `${invitation.firstName || ''} ${invitation.lastName || ''}`.trim()
        if (newDisplayName) {
          updateFields.displayName = newDisplayName
        }
      }

      // Update phoneNumber from invitation if provided
      if (invitation.phoneNumber) {
        updateFields.phoneNumber = invitation.phoneNumber
        if (invitation.phoneCountry) {
          updateFields.phoneCountry = invitation.phoneCountry
        }
      }

      // Security-critical fields (role, corporateAccountId, parentUserId, createdBy)
      // can only be updated by Admin SDK (Firestore rules block self-modification).
      // The bypass rule in firestore.rules (users update, ~line 195) ONLY fires
      // when `corporateAccountId == null OR role == null`. If both are populated,
      // ANY write to role/corporateAccountId/createdBy/parentUserId in this update
      // causes the entire updateDoc to fail rules — even if you're only "filling
      // in" a previously-null field like parentUserId.
      //
      // Failure mode (D-N-014): a self-registered admin (createdBy=null AND
      // parentUserId=null, but role+corporateAccountId populated) gets soft-deleted
      // and re-invited. The per-field `if (!existingData.X)` guards below would
      // try to backfill createdBy + parentUserId, the bypass rule won't permit it
      // (because corporateAccountId+role are non-null), and the entire reactivation
      // updateDoc throws — leaving the user stranded in `accountStatus: 'deleted'`.
      //
      // Fix: only attempt security-critical-field writes when the bypass rule
      // would actually allow them. Otherwise leave them alone — existing values
      // are correct, and onInvitationAccepted Cloud Function can fix any drift
      // via Admin SDK.
      const bypassRuleAllows =
        !existingData.corporateAccountId || !existingData.role

      if (bypassRuleAllows) {
        if (!existingData.role) {
          updateFields.role = invitation.role
          logger.debug(
            '[acceptOAuthInvitation] Will set role:',
            invitation.role
          )
        }

        if (!existingData.corporateAccountId) {
          updateFields.corporateAccountId = invitation.corporateAccountId
          // Proves membership to the rules (RS-2 step 5)
          updateFields.invitationId = invitationId
          logger.debug(
            '[acceptOAuthInvitation] Will set corporateAccountId:',
            invitation.corporateAccountId
          )
        }

        if (!existingData.createdBy) {
          updateFields.createdBy = effectiveCreatedBy
          logger.debug(
            '[acceptOAuthInvitation] Will set createdBy:',
            effectiveCreatedBy
          )
        }

        if (!existingData.parentUserId) {
          updateFields.parentUserId = effectiveParentUserId
          logger.debug(
            '[acceptOAuthInvitation] Will set parentUserId:',
            effectiveParentUserId
          )
        }
      } else {
        logger.debug(
          '[acceptOAuthInvitation] Bypass rule not available (existing user has role + corporateAccountId populated) — skipping security-critical field backfill. Cloud Function onInvitationAccepted will handle any drift via Admin SDK.',
          {
            existingRole: existingData.role,
            existingCorporateAccountId: existingData.corporateAccountId,
            existingCreatedBy: existingData.createdBy,
            existingParentUserId: existingData.parentUserId,
          }
        )
      }

      // Log what will be updated by Cloud Function (Admin SDK) instead
      logger.debug(
        '[acceptOAuthInvitation] Deferred to Cloud Function (Admin SDK):',
        {
          corporateAccountId:
            existingData.corporateAccountId !== invitation.corporateAccountId
              ? `${existingData.corporateAccountId} → ${invitation.corporateAccountId}`
              : 'already correct',
          parentUserId:
            existingData.parentUserId !== effectiveParentUserId
              ? `${existingData.parentUserId} → ${effectiveParentUserId}`
              : 'already correct',
          role:
            existingData.role !== invitation.role
              ? `${existingData.role} → ${invitation.role}`
              : 'already correct',
        }
      )

      logger.debug('[acceptOAuthInvitation] Will set accountStatus: active')

      await updateDoc(userRef, updateFields)
      logger.debug(
        isReactivation
          ? '[acceptOAuthInvitation] ✅ User reactivated successfully'
          : '[acceptOAuthInvitation] ✅ User document updated'
      )
    }

    // Step 4b: Create user's own relationship document (for future hierarchy if needed)
    // Note: We only create the user's OWN document here. The inviter's children array
    // is updated by onInvitationAccepted (server, PB-67); the rules refuse the tenant.
    logger.debug(
      '[acceptOAuthInvitation] Step 4b: Creating user relationship document...'
    )
    try {
      const userRelationshipRef = doc(db, 'userRelationships', userId)
      const userRelationshipDoc = await getDoc(userRelationshipRef)
      if (!userRelationshipDoc.exists()) {
        await setDoc(userRelationshipRef, {
          children: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
        logger.debug(
          '[acceptOAuthInvitation] ✅ Created user relationship document'
        )
      }
    } catch (relationshipError) {
      console.warn(
        '[acceptOAuthInvitation] ⚠️ Failed to create user relationship document:',
        relationshipError
      )
      // Continue - user doc was created/updated successfully, relationships can be set up later
    }

    // Update user with invitation-specific data
    logger.debug(
      '[acceptOAuthInvitation] Step 5: Updating user with invitation-specific data...'
    )
    if (
      invitation.hasAdminPrivileges ||
      invitation.assignedProperties?.length ||
      invitation.metadata
    ) {
      await updateUserWithInvitationData(userId, invitation, {})
      logger.debug(
        '[acceptOAuthInvitation] ✅ Invitation-specific data applied'
      )
    }

    // Mark invitation as accepted
    logger.debug(
      '[acceptOAuthInvitation] Step 6: Marking invitation as accepted...'
    )
    await updateInvitationStatus(invitationId, 'accepted', {
      acceptedBy: userId,
      acceptedAt: serverTimestamp() as Timestamp,
    })
    logger.debug('[acceptOAuthInvitation] ✅ Invitation marked as accepted')

    // Server-side: verify email + send E12 notification to inviter (non-blocking)
    // Done via Cloud Function because:
    // 1. Only Admin SDK can set emailVerified on Firebase Auth user
    // 2. Tenants can't write 'invitation-accepted' type to mail collection (Firestore rules)
    try {
      const { auth: firebaseAuth } = await import('../firebase')
      const idToken = await firebaseAuth.currentUser?.getIdToken()
      if (idToken) {
        fetch(functionUrl('onInvitationAccepted'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ invitationId }),
        }).catch(err =>
          console.error(
            '[acceptOAuthInvitation] Cloud Function call failed:',
            err
          )
        )
      }
    } catch (notifyErr) {
      console.error(
        '[acceptOAuthInvitation] Error calling onInvitationAccepted:',
        notifyErr
      )
    }

    logger.debug(
      '[acceptOAuthInvitation] ✅ SUCCESS - Invitation accepted successfully'
    )
    return { success: true }
  } catch (error) {
    console.error('[acceptOAuthInvitation] ❌ ERROR:', error)

    // ROLLBACK: Delete user doc if we created it
    if (userDocCreated) {
      try {
        await deleteDoc(doc(db, 'users', userId))
        logger.debug('[acceptOAuthInvitation] ⚠️ Rolled back user creation')
      } catch (rollbackError) {
        console.error(
          '[acceptOAuthInvitation] ❌ Rollback failed:',
          rollbackError
        )
      }
    }

    return { success: false, error: (error as Error).message }
  }
}

/**
 * Accepts an invitation and creates user account
 */
export const acceptInvitation = async (
  invitationId: string,
  userRegistrationData: {
    firstName: string
    lastName: string
    password: string
    phoneNumber?: string
    timezone?: string
  }
): Promise<{ success: boolean; userId?: string; error?: string }> => {
  try {
    // Verify invitation is still valid
    const verificationResult = await verifyEnhancedInvitation(invitationId)
    if (!verificationResult.success || !verificationResult.invitation) {
      return { success: false, error: verificationResult.error }
    }

    const invitation = verificationResult.invitation

    // Import registration function
    const { registerWithMinimalData } = await import('./auth')

    // Register user with invitation data
    const registrationResult = await registerWithMinimalData(
      {
        firstName: userRegistrationData.firstName,
        lastName: userRegistrationData.lastName,
        email: invitation.email,
        password: userRegistrationData.password,
        role: invitation.role,
      },
      invitation.invitedBy,
      { skipEmailVerification: true }
    )

    if (!registrationResult.success || !registrationResult.user) {
      return { success: false, error: registrationResult.error }
    }

    // Update user with invitation-specific data
    if (
      invitation.hasAdminPrivileges ||
      invitation.assignedProperties?.length ||
      invitation.metadata
    ) {
      await updateUserWithInvitationData(
        registrationResult.user.uid,
        invitation,
        userRegistrationData
      )
    }

    // Mark invitation as accepted
    await updateInvitationStatus(invitationId, 'accepted', {
      acceptedBy: registrationResult.user.uid,
      acceptedAt: serverTimestamp() as Timestamp,
    })

    return { success: true, userId: registrationResult.user.uid }
  } catch (error) {
    console.error('Error accepting invitation:', error)
    return { success: false, error: (error as Error).message }
  }
}

/**
 * Updates user document with invitation-specific data
 */
const updateUserWithInvitationData = async (
  userId: string,
  invitation: EnhancedInvitation,
  userData: {
    phoneNumber?: string
    timezone?: string
  }
): Promise<void> => {
  const userRef = doc(db, 'users', userId)
  const updateData: Record<string, unknown> = {}

  // Add admin privileges if specified
  if (invitation.hasAdminPrivileges) {
    updateData.hasAdminPrivileges = true
  }

  // Copy custom permissions from invitation to user doc
  if (invitation.permissions) {
    updateData.permissions = invitation.permissions
  }

  // Add phone and timezone if provided
  if (userData.phoneNumber) {
    updateData.phoneNumber = userData.phoneNumber
  }

  if (userData.timezone) {
    updateData.timezone = userData.timezone
  }

  // Add organization context
  if (invitation.organizationId) {
    updateData.organizationId = invitation.organizationId
  }

  // Add role-specific assignments
  if (
    invitation.role === UserRoles.MANAGEMENT &&
    invitation.assignedProperties?.length
  ) {
    updateData.managedProperties = invitation.assignedProperties
  }

  if (
    invitation.role === UserRoles.TENANT &&
    invitation.assignedProperties?.length
  ) {
    updateData.assignedProperties = invitation.assignedProperties
    // Also set assignedPropertyId for compatibility with TenantsTable and other components
    // that expect a single property ID field (tenants can only have one property)
    updateData.assignedPropertyId = invitation.assignedProperties[0]
    // Track who assigned this tenant — use resolved admin when super_admin created the invitation
    updateData.assignedByUserId =
      invitation.resolvedAdminId || invitation.invitedBy

    logger.debug('[updateUserWithInvitationData] Tenant property assignment:', {
      userId,
      assignedPropertyId: invitation.assignedProperties[0],
      assignedByUserId: updateData.assignedByUserId,
      resolvedAdminId: invitation.resolvedAdminId || 'N/A',
      corporateAccountId: invitation.corporateAccountId,
    })

    // Create propertyAssignments documents for tenant
    await createPropertyAssignmentsForTenant(
      userId,
      invitation.assignedProperties,
      invitation.metadata?.unit
    )
  }

  // Add metadata fields
  if (invitation.metadata?.department) {
    updateData.department = invitation.metadata.department
  }

  updateData.updatedAt = serverTimestamp()

  await updateDoc(userRef, updateData)
}

/**
 * Creates tenant property assignments with default HVAC equipment for testing.
 * Uses tenantPropertyAssignments collection to match PropertyManagementService.getPropertyTenants().
 */
const createPropertyAssignmentsForTenant = async (
  userId: string,
  propertyIds: string[],
  unit?: string
): Promise<void> => {
  for (const propertyId of propertyIds) {
    // Fetch property details
    const propertyRef = doc(db, 'properties', propertyId)
    const propertySnap = await getDoc(propertyRef)

    if (!propertySnap.exists()) {
      console.warn(`Property ${propertyId} not found`)
      continue
    }

    const property = propertySnap.data()

    // Create default HVAC equipment (overdue for testing)
    const now = new Date()
    const hvacEquipment = [
      {
        id: `hvac-${Date.now()}`,
        serialNumber: `SN-${Math.random().toString(36).substring(7).toUpperCase()}`,
        manufacturer: 'Generic',
        model: 'Standard Unit',
        type: 'central_ac',
        filterSize: '20x25x1',
        location: unit ? `Unit ${unit}` : 'Main Unit',
        installDate: Timestamp.fromDate(new Date('2020-01-01')),

        // Maintenance schedule - OVERDUE for testing
        frequency: 90,
        lastChanged: Timestamp.fromDate(
          new Date(now.getTime() - 120 * 24 * 60 * 60 * 1000)
        ), // 120 days ago
        nextChangeDate: Timestamp.fromDate(
          new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        ), // 30 days ago
        maintenanceStatus: 'overdue',

        // Climate data
        climateZone: property.climateZone || 'moderate',
        climateData: {
          zone: property.climateZone || 'moderate',
          avgTemp: 75,
          avgHumidity: 50,
          usagePattern: 'normal',
          recommendedFrequency: 90,
        },
      },
    ]

    // Create tenant property assignment document in tenantPropertyAssignments collection
    // Schema matches PropertyManagementService.assignTenantToProperty()
    const assignmentRef = doc(collection(db, 'tenantPropertyAssignments'))
    // RS-2: the assignment carries its property's account; the rules
    // require it on the tenant's own create during acceptance.
    const propertyAccountId =
      typeof property.corporateAccountId === 'string' &&
      property.corporateAccountId !== 'default'
        ? property.corporateAccountId
        : null
    const assignmentData = {
      id: assignmentRef.id,
      ...(propertyAccountId && { corporateAccountId: propertyAccountId }),
      tenantId: userId, // Use tenantId field to match PropertyManagementService schema
      propertyId,
      managerId: property.managedBy || property.currentManagerId || null,
      unitNumber: unit || '',
      assignedBy: 'invitation_system',
      startDate: serverTimestamp(),
      endDate: null,
      leaseStartDate: null,
      leaseEndDate: null,
      moveInDate: null,
      status: 'active',
      createdAt: serverTimestamp(),
      // Additional data for tenant's HVAC maintenance view
      hvacEquipment,
      propertyData: {
        name: property.name || '',
        address: property.address || '',
        city: property.city || '',
        state: property.state || '',
        zipCode: property.zipCode || '',
      },
    }

    await setDoc(assignmentRef, assignmentData)
    logger.debug(
      `[createPropertyAssignmentsForTenant] Created tenantPropertyAssignment for tenant ${userId} to property ${propertyId}`
    )

    // Note: tenantIds update on property doc is handled by onInvitationAccepted Cloud Function
    // (Admin SDK has permission to write to properties; tenant auth context does not)

    // Also update the user document with currentProperty reference
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        currentProperty: propertyId,
        currentManager: property.managedBy || property.currentManagerId || null,
        unitNumber: unit || '',
        updatedAt: serverTimestamp(),
      })
      logger.debug(`Updated user ${userId} with currentProperty: ${propertyId}`)
    } catch (userUpdateError) {
      console.warn(
        `Could not update user currentProperty (may already be set):`,
        userUpdateError
      )
    }
  }
}

/**
 * Bulk operations for invitation management
 */
export const bulkInviteUsers = async (
  invitations: CreateInvitationData[],
  createdByUid: string
): Promise<{
  success: boolean
  results: Array<{
    email: string
    success: boolean
    invitationId?: string
    error?: string
  }>
  error?: string
}> => {
  try {
    const results = []

    for (const invitationData of invitations) {
      const result = await createEnhancedInvitation(
        invitationData,
        createdByUid
      )

      results.push({
        email: invitationData.email,
        success: result.success,
        invitationId: result.invitation?.id,
        error: result.error,
      })
    }

    return {
      success: true,
      results,
    }
  } catch (error) {
    console.error('Error bulk inviting users:', error)
    return { success: false, results: [], error: (error as Error).message }
  }
}

/**
 * Gets onboarding flow for role
 */
function getOnboardingFlow(role: UserRole): string[] {
  const flows: Record<UserRole, string[]> = {
    [UserRoles.SUPER_ADMIN]: [
      'account-setup',
      'system-overview',
      'user-management',
    ],
    [UserRoles.CORPORATE]: [
      'company-profile',
      'billing-setup',
      'user-management',
      'property-overview',
    ],
    [UserRoles.ADMIN]: [
      'profile-completion',
      'user-management',
      'property-management',
    ],
    [UserRoles.MANAGEMENT]: [
      'profile-completion',
      'property-assignment',
      'tenant-management',
    ],
    [UserRoles.TENANT]: [
      'profile-completion',
      'property-information',
      'submission-requirements',
    ],
  }

  return flows[role] || ['profile-completion']
}

/**
 * Gets required fields for role completion
 */
function getRequiredFields(role: UserRole): string[] {
  const fields: Record<UserRole, string[]> = {
    [UserRoles.SUPER_ADMIN]: ['firstName', 'lastName', 'phoneNumber'],
    [UserRoles.CORPORATE]: [
      'firstName',
      'lastName',
      'phoneNumber',
      'corporationName',
      'billingAddress',
    ],
    [UserRoles.ADMIN]: [
      'firstName',
      'lastName',
      'phoneNumber',
      'jobTitle',
      'department',
    ],
    [UserRoles.MANAGEMENT]: [
      'firstName',
      'lastName',
      'phoneNumber',
      'licenseNumber',
      'managedProperties',
    ],
    [UserRoles.TENANT]: [
      'firstName',
      'lastName',
      'phoneNumber',
      'emergencyContact',
      'employmentStatus',
    ],
  }

  return fields[role] || ['firstName', 'lastName', 'phoneNumber']
}

/**
 * Clean up expired invitations
 */
export const cleanupExpiredInvitations = async (): Promise<{
  success: boolean
  cleanedCount: number
  error?: string
}> => {
  try {
    const now = new Date()
    const expiredQuery = query(
      collection(db, 'invitations'),
      ...(await currentAccountConstraints()),
      where('status', '==', 'pending')
    )

    const querySnapshot = await getDocs(expiredQuery)
    const batch = writeBatch(db)
    let cleanedCount = 0

    querySnapshot.forEach(docSnapshot => {
      const invitation = docSnapshot.data() as EnhancedInvitation
      if (invitation.expiresAt.toDate() < now) {
        batch.update(doc(db, 'invitations', docSnapshot.id), {
          status: 'expired',
          updatedAt: serverTimestamp(),
        })
        cleanedCount++
      }
    })

    if (cleanedCount > 0) {
      await batch.commit()
    }

    return { success: true, cleanedCount }
  } catch (error) {
    console.error('Error cleaning up expired invitations:', error)
    return { success: false, cleanedCount: 0, error: (error as Error).message }
  }
}

/**
 * Delete cancelled (revoked) invitations permanently from Firestore
 */
export const deleteCancelledInvitations = async (
  invitationIds: string[]
): Promise<{
  success: boolean
  deletedCount: number
  error?: string
}> => {
  try {
    if (invitationIds.length === 0) {
      return { success: true, deletedCount: 0 }
    }

    const batch = writeBatch(db)

    invitationIds.forEach(id => {
      batch.delete(doc(db, 'invitations', id))
    })

    await batch.commit()

    return { success: true, deletedCount: invitationIds.length }
  } catch (error) {
    console.error('Error deleting cancelled invitations:', error)
    return { success: false, deletedCount: 0, error: (error as Error).message }
  }
}
